<template>
  <v-container v-if="loading" fluid fill-height class="white">
    <loading></loading>
  </v-container>
  <v-container v-else-if="loadError" fluid fill-height class="white">
    <v-layout row wrap align-content-start pa-2>
      <v-flex xs12>
        <h1 class="text-xs-center">Plant Alerts</h1>
      </v-flex> 
      <v-flex xs12>
        <v-alert 
          :value="true" 
          outline 
          type="error">
          <span>{{ loadError }}</span>
        </v-alert>
      </v-flex>
    </v-layout>
  </v-container>
  <v-form v-else class="fill-height" v-model="formValid">
    <v-container fluid fill-height class="white">
        <v-layout row wrap align-content-start pa-2>
          <v-flex xs12>
            <h1 class="text-xs-center">Plant Alerts</h1>
          </v-flex>
          <v-flex v-if="saveError" xs12>
            <v-alert 
              :value="true" 
              outline 
              type="error">
              <span>{{ saveError }}</span>
            </v-alert>
          </v-flex>
          <v-flex xs12>
            <v-btn color="primary" @click="saveChanges" :loading="saving">
              <span>Save Changes</span>
            </v-btn>
          </v-flex>
          <template v-if="showSessionFields">
            <v-flex xs12>
              <v-card>
                <v-toolbar card dark ripple color="primary" @click="minimumQualityScoreEnabled = !minimumQualityScoreEnabled">
                  <v-btn icon>
                    <v-icon v-if="minimumQualityScoreEnabled">check_box</v-icon>
                    <v-icon v-else>check_box_outline_blank</v-icon>
                  </v-btn>
                  <v-toolbar-title>Minimum Quality Score</v-toolbar-title>
                  <v-spacer></v-spacer>
                </v-toolbar>
                  <v-layout v-if="minimumQualityScoreEnabled" row wrap align-content-start pa-2>
                    <v-flex xs6 class="pa-2">
                      <v-text-field
                        label="Minimum Quality Score"
                        :disabled="!minimumQualityScoreEnabled"
                        :rules="[validationRules.number({ min: 0, max: 9999, decimalPlaces: 3 })]"
                        v-model="minimumQualityScore">
                      </v-text-field>
                    </v-flex>
                    <v-flex xs6 class="pa-2">
                      <v-text-field
                        label="Snooze Period"
                        suffix="hr"
                        :disabled="!minimumQualityScoreEnabled"
                        :rules="[validationRules.number({ min: 0, max: 9999, decimalPlaces: 3 })]"
                        v-model="minimumQualityScoreSnoozePeriod">
                      </v-text-field>
                    </v-flex>
                  </v-layout>
              </v-card>
            </v-flex>
            <v-flex xs12>
              <v-card>
                <v-toolbar card dark ripple color="primary" @click="pickRateVarianceEnabled = !pickRateVarianceEnabled">
                  <v-btn icon>
                    <v-icon v-if="pickRateVarianceEnabled">check_box</v-icon>
                    <v-icon v-else>check_box_outline_blank</v-icon>
                  </v-btn>
                  <v-toolbar-title>Pick Rate Variance</v-toolbar-title>
                  <v-spacer></v-spacer>
                </v-toolbar>
                <v-layout v-if="pickRateVarianceEnabled" row wrap align-content-start pa-2>
                  <v-flex xs6 class="pa-2">
                    <v-text-field
                      label="Pick Rate Variance"
                      suffix="%"
                      :disabled="!pickRateVarianceEnabled"
                      :rules="[validationRules.number({ min: 0, max: 9999, decimalPlaces: 5 })]"
                      v-model="pickRateVariance">
                    </v-text-field>
                  </v-flex>
                  <v-flex xs6 class="pa-2">
                    <v-text-field
                      label="Snooze Period"
                      suffix="hr"
                      :disabled="!pickRateVarianceEnabled"
                      :rules="[validationRules.number({ min: 0, max: 9999, decimalPlaces: 3 })]"
                      v-model="pickRateVarianceSnoozePeriod">
                    </v-text-field>
                  </v-flex>
                </v-layout>
              </v-card>
            </v-flex>
            <v-flex xs12>
              <v-card>
                <v-toolbar card dark ripple color="primary" @click="maximumSecondsEnabled = !maximumSecondsEnabled">
                  <v-btn icon>
                    <v-icon v-if="maximumSecondsEnabled">check_box</v-icon>
                    <v-icon v-else>check_box_outline_blank</v-icon>
                  </v-btn>
                  <v-toolbar-title>Maximum Seconds</v-toolbar-title>
                  <v-spacer></v-spacer>
                </v-toolbar>
                <v-layout v-if="maximumSecondsEnabled" row wrap align-content-start pa-2>
                  <v-flex xs6 class="pa-2">
                    <v-text-field
                      label="Default maximum seconds % threshold"
                      suffix="%"
                      :disabled="!maximumSecondsEnabled"
                      :rules="[validationRules.number({ min: 0, max: 100, decimalPlaces: 0 })]"
                      v-model="maximumSeconds">
                    </v-text-field>
                  </v-flex>
                  <v-flex xs6 class="pa-2">
                    <v-text-field
                      label="Default alert snooze period"
                      suffix="hr"
                      :disabled="!maximumSecondsEnabled"
                      :rules="[validationRules.number({ min: 0, max: 9999, decimalPlaces: 3 })]"
                      v-model="maximumSecondsSnoozePeriod">
                    </v-text-field>
                  </v-flex>
                  <v-flex xs12 class="pa-2">
                    <v-btn color="primary" @click="openMaximumSecondsDialog">
                      <span>Configure Locations and Varieties</span>
                    </v-btn>
                  </v-flex>
                </v-layout>
                <v-dialog v-if="maximumSecondsDialogVisible" :value="true" persistent fullscreen @keydown.esc.stop="closeMaximumSecondsDialog">
                  <PlantConfigurationAlertSecondsDialog
                    :plantID="plantID"
                    @close="closeMaximumSecondsDialog"
                    @confirm="confirmMaximumSecondsDialog">
                  </PlantConfigurationAlertSecondsDialog>
                </v-dialog>
              </v-card>
            </v-flex>
          </template>
        </v-layout>
    </v-container>
  </v-form>
</template>

<script>
import BaseAPI from "@/api/baseApi";
import plantConfigurationApi from '@/api/plantConfigurationApi';
import Loading from '@/components/generic/Loading.vue';
import PlantConfigurationAlertSecondsDialog from '@/components/plants/configuration/PlantConfigurationAlertSecondsDialog.vue';
import validationRules from '@/helpers/validationrules';
import utilService from '@/services/utilService';
const formatNumber = utilService.formatNumber;

export default {
  components: {
    Loading,
    PlantConfigurationAlertSecondsDialog
  },
  props: {
    plantID: {
      required: true,
      type: Number
    }
  },
  data() {
    return {
      validationRules: validationRules.rules,
      loading: false,
      loadError: null,
      formValid: false,
      showSessionFields: false,
      minimumQualityScoreEnabled: false,
      minimumQualityScore: null,
      minimumQualityScoreSnoozePeriod: null,
      pickRateVarianceEnabled: false,
      pickRateVariance: null,
      pickRateVarianceSnoozePeriod: null,
      maximumSecondsEnabled: false,
      maximumSeconds: null,
      maximumSecondsSnoozePeriod: null,
      maximumSecondsDialogVisible: false,
      saving: false,
      saveError: null
    }
  },
  methods: {
    async init(){
      await this.loadData();
    },
    async loadData(){
      this.loading = true;

      this.loadError = null;
      this.resetPlantAlertSettings();
      try {
        let plantAlertSettings = await plantConfigurationApi.getPlantAlertSettings(this.plantID);

        this.loadPlantAlertSettings(plantAlertSettings);
      } catch (err){
        this.loadError = await BaseAPI.parseHttpRequestError(err);
        
        console.log(err);
      }

      this.loading = false;
    },
    resetPlantAlertSettings(){
      this.showSessionFields = false;
      this.minimumQualityScoreEnabled = false;
      this.minimumQualityScore = null;
      this.minimumQualityScoreSnoozePeriod = null;
      this.pickRateVarianceEnabled = false;
      this.pickRateVariance = null;
      this.pickRateVarianceSnoozePeriod = null;
      this.maximumSecondsEnabled = false;
      this.maximumSeconds = null;
      this.maximumSecondsSnoozePeriod = null;
    },
    loadPlantAlertSettings(plantAlertSettings){
      this.showSessionFields = plantAlertSettings.ShowSessionFields;
      this.minimumQualityScoreEnabled = plantAlertSettings.MinimumQualityScoreEnabled;
      this.minimumQualityScore = formatNumber(plantAlertSettings.MinimumQualityScore, 3);
      this.minimumQualityScoreSnoozePeriod = formatNumber(plantAlertSettings.MinimumQualityScoreSnoozePeriod, 3);
      this.pickRateVarianceEnabled = plantAlertSettings.PickRateVarianceEnabled;
      this.pickRateVariance = formatNumber(plantAlertSettings.PickRateVariance == null ? null : plantAlertSettings.PickRateVariance * 100, 3);
      this.pickRateVarianceSnoozePeriod = formatNumber(plantAlertSettings.PickRateVarianceSnoozePeriod, 3);
      this.maximumSecondsEnabled = plantAlertSettings.MaximumSecondsEnabled;
      this.maximumSeconds = formatNumber(plantAlertSettings.MaximumSeconds == null ? null : plantAlertSettings.MaximumSeconds * 100, 0);
      this.maximumSecondsSnoozePeriod = formatNumber(plantAlertSettings.MaximumSecondsSnoozePeriod, 3);
    },
    openMaximumSecondsDialog(){
      this.maximumSecondsDialogVisible = true;
    },
    closeMaximumSecondsDialog(){
      this.maximumSecondsDialogVisible = false;
    },
    confirmMaximumSecondsDialog(){
      this.closeMaximumSecondsDialog();
    },
    async saveChanges(){
      this.saving = true;

      this.saveError = null;
      try {
        let plantAlertSettings = {
          MinimumQualityScoreEnabled: this.minimumQualityScoreEnabled,
          MinimumQualityScore: this.minimumQualityScore,
          MinimumQualityScoreSnoozePeriod: this.minimumQualityScoreSnoozePeriod,
          PickRateVarianceEnabled: this.pickRateVarianceEnabled,
          PickRateVariance: this.pickRateVariance == null ? null : this.pickRateVariance / 100,
          PickRateVarianceSnoozePeriod: this.pickRateVarianceSnoozePeriod,
          MaximumSecondsEnabled: this.maximumSecondsEnabled,
          MaximumSeconds: this.maximumSeconds == null || this.maximumSeconds === "" ? null : this.maximumSeconds / 100,
          MaximumSecondsSnoozePeriod: this.maximumSecondsSnoozePeriod
        };

        let result = await plantConfigurationApi.savePlantAlertSettings(this.plantID, plantAlertSettings);
        if(!result.Success){
          this.saveError = result.ErrorMessages.join("\n");

          console.log(result.ErrorMessages)
        }
      } catch (err){
        this.saveError = await BaseAPI.parseHttpRequestError(err);

        console.log(err);
      }

      this.saving = false;
    }
  },
  created(){
    this.init();
  }
}
</script>